import { dateTimeDisplayFormat } from '@/config';
import { t } from 'i18next';
import { formatCurrency } from '@/helpers/format';
import { getFullName } from '@/helpers/string';
import bixolonLabelPrinterService, { labelPrinterCommand } from './bixolonLabelPrinterService';
import moment from 'moment';
import { Modal } from 'antd';
import { StationPrinterInfoModel, BookingDetailsModel, BookingTicketDetailsModel } from '@/__generated';

const showErrorPrinterModal = () => {
  Modal.error({
    get title() { return t('Error'); },
    get content() { return t('Can not connect to printer'); },
  });
};
class BixolonWebPrintService {
  getAvailablePrinter = (printerType: number, printers: StationPrinterInfoModel[] | null | undefined) => {
    return printers?.find(x => x.printType === printerType);
  };

  // label printer
  loadImageToCanvas = (imageUrl: string) => {
    return new Promise(resolve => {
      try {
        const canvasElm = document.createElement('canvas');
        const context = canvasElm.getContext('2d');
        const image = new Image();
        image.src = imageUrl;
        image.setAttribute('crossorigin', 'anonymous'); // works for me

        image.onload = (evt: any) => {
          if (evt.target && context) {
            canvasElm.width = evt.target.width || 0;
            canvasElm.height = evt.target.height || 0;

            context.drawImage(image, 0, 0);
            canvasElm.width = image.height;
            canvasElm.height = image.width;
            context.translate(image.height / 2, image.width / 2);

            context.rotate((90 * Math.PI) / 180);
            context.drawImage(image, -image.width / 2, -image.height / 2);

            return resolve(canvasElm.toDataURL());
          }
          return resolve(null);
        };
      } catch (error) {
        return resolve(null);
      }
    });
  };
  drawSingleTicket = async (
    printer: StationPrinterInfoModel,
    bookingDetail: BookingDetailsModel,
    ticket: BookingTicketDetailsModel,
    isDeparture: boolean,
  ) => {
    printer.companyLogoImageUrl = 'http://ditransportadminstaging.conekto.com:3389/static/media/g152.3b75d368c0e48ee64093.png';
    const config = labelPrinterCommand.__config;
    const cmd = labelPrinterCommand;
    const functions: Array<any> = [];
    functions.push(cmd.clearBuffer());
    functions.push(cmd.setWidth(576));
    functions.push(cmd.setPrintingType('direct'));
    const marginLeft = 20;
    const sourceStation = (isDeparture ? bookingDetail.sourceStation : bookingDetail.destinationStation)?.toUpperCase() || '';
    const toStation = (isDeparture ? bookingDetail.destinationStation : bookingDetail.sourceStation)?.toUpperCase() || '';
    const departureDateTime =
      dateTimeDisplayFormat.originDateTime(isDeparture ? bookingDetail.departureDateTime : bookingDetail.returnDepartureDateTime) || '';
    const returnDate =
      dateTimeDisplayFormat.originDateTimeNoYear(isDeparture ? bookingDetail.arrivalDateTime : bookingDetail.returnArrivalDateTime) || '';
    const checkinDateTime = moment(isDeparture ? bookingDetail.departureDateTime : bookingDetail.returnDepartureDateTime).add(
      0 - (bookingDetail.tripBoardingLimitTime || 0),
      'm',
    );
    const bookingDate = dateTimeDisplayFormat.dateTime(bookingDetail.bookingDate);
    const sourceCountry = (isDeparture ? bookingDetail.sourceCountry : bookingDetail.destinationCountry) || '';
    const toCountry = (isDeparture ? bookingDetail.destinationCountry : bookingDetail.sourceCountry) || '';

    const checkinTime = `Check in time: ${dateTimeDisplayFormat.originDateTime(checkinDateTime) || ''}`;
    const returnDateTime =
      dateTimeDisplayFormat.originDateTime(isDeparture ? bookingDetail.arrivalDateTime : bookingDetail.returnArrivalDateTime) || '';
    const seat = ticket.seats?.find(x => x.sequenceNumber === (isDeparture ? 1 : 2));

    functions.push(
      cmd.printText(printer.companyDisplayName || '', 480, 420, config.fontType.s18, config.rotation.deg90, true, config.alignment.left),
    );
    const extendWidth = 650;
    const passengerMarginLeft = extendWidth + marginLeft;
    if (printer.companyLogoImageUrl) {
      try {
        const logoData: any = await this.loadImageToCanvas(printer.companyLogoImageUrl);
        if (logoData) {
          functions.push(cmd.printImage(logoData, 460, marginLeft, 40));
          functions.push(cmd.printImage(logoData, 460, passengerMarginLeft, 40));
        }
      } catch (err) {
        console.log(err);
      }
    }
    functions.push(cmd.printText('Departure', 380, marginLeft, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(sourceStation, 320, marginLeft, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(sourceCountry, 280, marginLeft, config.fontType.s12, config.rotation.deg90, false, config.alignment.left));

    functions.push(cmd.printText(departureDateTime, 230, marginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));

    //
    functions.push(cmd.printText('Arrival', 380, 320, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));

    functions.push(cmd.printText(toStation, 320, 320, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(toCountry, 280, 320, config.fontType.s12, config.rotation.deg90, false, config.alignment.left));
    functions.push(cmd.printText(returnDate, 230, 320, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));
    //

    const price = `Price: ${formatCurrency(bookingDetail.totalAmount)}`;
    functions.push(
      cmd.printText(`Seat: ${seat?.seatName}`, 180, marginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left),
    );
    functions.push(cmd.printText(price, 140, marginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));
    //
    const fullName = getFullName(ticket.passengerFirstName, ticket.passengerLastName);
    functions.push(cmd.printText(fullName, 100, marginLeft, config.fontType.s8, config.rotation.deg90, false, config.alignment.left));
    const bookingRefNo = 'Booking Ref: ' + bookingDetail.bookingRefNo;
    functions.push(cmd.printText(bookingRefNo, 70, marginLeft, config.fontType.s8, config.rotation.deg90, false, config.alignment.left));
    const ticketRef = 'Ticket Ref: ' + bookingDetail.bookingRefNo;
    functions.push(cmd.printText(ticketRef, 40, marginLeft, config.fontType.s8, config.rotation.deg90, false, config.alignment.left));
    //
    functions.push(cmd.printQRCode(ticket.ticketRefNo || '', 20, 400, config.qrCodeSize.s8, config.rotation.None));

    // passenger ticket

    functions.push(
      cmd.printText(
        printer.companyDisplayName || '',
        480,
        passengerMarginLeft + 420,
        config.fontType.s18,
        config.rotation.deg90,
        true,
        config.alignment.left,
      ),
    );
    // Departure info
    functions.push(cmd.printText(bookingDate, 500, extendWidth + 700, config.fontType.s10, config.rotation.deg90, true, config.alignment.left));

    functions.push(cmd.printText('Departure', 380, passengerMarginLeft, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(sourceStation, 320, passengerMarginLeft, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(sourceCountry, 280, passengerMarginLeft, config.fontType.s12, config.rotation.deg90, false, config.alignment.left));

    functions.push(
      cmd.printText(departureDateTime, 220, passengerMarginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left),
    );

    // center info
    const ticketClassText = `${seat?.ticketClassName} : ${seat?.ticketConditionName}`;
    functions.push(
      cmd.printText(ticketClassText, 350, passengerMarginLeft + 230, config.fontType.s10, config.rotation.deg90, false, config.alignment.left),
    );
    const arrowText = '---------------->';
    functions.push(
      cmd.printText(arrowText, 320, passengerMarginLeft + 250, config.fontType.s10, config.rotation.deg90, false, config.alignment.left),
    );

    functions.push(
      cmd.printText(
        `Seat: ${seat?.seatName}`,
        300,
        passengerMarginLeft + 300,
        config.fontType.s10,
        config.rotation.deg90,
        true,
        config.alignment.left,
      ),
    );

    // destination info
    const returnY = passengerMarginLeft + 600;
    functions.push(cmd.printText('Arrival', 380, returnY, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(toStation, 320, returnY, config.fontType.s12, config.rotation.deg90, true, config.alignment.left));
    functions.push(cmd.printText(toCountry, 280, returnY, config.fontType.s12, config.rotation.deg90, false, config.alignment.left));
    functions.push(cmd.printText(returnDateTime, 220, returnY, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));

    // footer info
    functions.push(cmd.printText(checkinTime, 160, passengerMarginLeft, config.fontType.s12, config.rotation.deg90, false, config.alignment.left));
    functions.push(cmd.printText(price, 110, passengerMarginLeft + 350, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));
    functions.push(cmd.printText(fullName, 110, passengerMarginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));

    functions.push(cmd.printText(bookingRefNo, 70, passengerMarginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));
    functions.push(cmd.printText(ticketRef, 30, passengerMarginLeft, config.fontType.s10, config.rotation.deg90, false, config.alignment.left));
    functions.push(cmd.printQRCode(ticket.ticketRefNo || '', 10, extendWidth + 700, config.qrCodeSize.s8, config.rotation.None));
    //
    functions.push(cmd.printBuffer());

    return functions;
  };
  printLabelTicket = async (printer: StationPrinterInfoModel | null | undefined, bookingDetail: BookingDetailsModel, ticketRefNo?: string) => {
    if (!printer) {
      showErrorPrinterModal();
      return;
    }
    const printerService = bixolonLabelPrinterService.init(printer.ipAddress || '', printer.deviceName || '');
    printerService.checkPrinterStatus().then(async res => {
      if (res) {
        const tickets = ticketRefNo ? bookingDetail.tickets?.filter(x => x.ticketRefNo === ticketRefNo) : bookingDetail.tickets;
        let functions: Array<any> = [];
        if (!tickets) {
          return;
        }
        for (let i = 0; i < tickets?.length; i++) {
          const ticket = tickets[i];
          functions = functions.concat(await this.drawSingleTicket(printer, bookingDetail, ticket, true));
          if (ticket.seats?.length === 2) {
            functions = functions.concat(await this.drawSingleTicket(printer, bookingDetail, ticket, false));
          }
        }
        printerService.callPrint(printerService.buildPrintActionFromArray(functions)).then(res => {
          console.log(res);
        });
      } else {
        showErrorPrinterModal();
      }
    });
  };
}

export default new BixolonWebPrintService();
